import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import {
  Button,
  TextField,
  DialogContent,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableRow,
  TableCell,
  Typography,
  Radio,
  DialogTitle,
  Box,
  Grid,
  ListSubheader
} from '@mui/material';

import { callRest, prettyDate, isManager, Id2Gql } from '@/common/utils/utils';
import { useNavigate } from 'react-router';
import { MembershipAgreementModal } from '@/views/Pages/Retail3/Modal/AddPayment/MembershipAgreementModal';
import { useError } from '@/hooks/useError';
import { Alert } from '@/RestoreComponent/Alert';
import { ModalActions } from '@/RestoreComponent/ModalActions';
import { DateInput } from '@/components/DateInput';
import { reasonTypes } from './EditMembership';

const useStyles: any = makeStyles(theme => ({
  root: {
    backgroundColor: '#F6FBFE',
    padding: '1rem '
  },
  root2: {
    padding: '1rem'
  },
  tableHeader: {
    backgroundColor: '#F6FBFE'
  },
  stateSelect: {
    justifyContent: 'center',
    margin: 'auto'
  },
  statusLabel: {
    color: theme.palette.text.primary
  },
  inputLabel: {
    color: theme.palette.text.secondary,
    marginBottom: '5px !important',
    marginLeft: '2px'
  },
  loading: {
    textAlign: 'center',
    margin: 'auto'
  },
  creditHeader: {
    color: theme.palette.primary[900],
    textDecoration: 'none'
  },
  priceColumn: {
    width: '100px',
    textAlign: 'right',
    paddingRight: '40px'
  },
  manualPriceInput: {
    maxWidth: '72px',
    resiz: {
      fontSize: '8'
    }
  }
}));

const LEGACY_MB_MEMBERSHIP_PI_ID = 111070;
const GRANDFATHERED_MEMBERSHIP_PI_ID = 110021009;

const now = new Date().toISOString().slice(0, 10);
const formattedDate = e => {
  return moment(e.target.value).utc().format('YYYY-MM-DD');
};

const ConvertMembershipModal = (props: any) => {
  const {
    oldMembership,
    setModalOpen,
    setRefreshList,
    isPaymentMethodOnFile,
    eventReasons
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [
    availableConversionOptionsInfoArray,
    setAvailableConversionOptionsInfoArray
  ] = useState([]);
  const [chosenMembershipId, setChosenMembershipId] = useState(null);
  const [discountMethod, setDiscountMethod] = useState('none');
  const [startDateOption, setStartDateOption] = useState<
    'today' | 'nextBill' | 'custom'
  >('today');
  const [startDate, setStartDate] = useState(now);
  const [convertReason, setConvertReason] = useState(undefined);
  const [creditSplit, setCreditSplit] = useState('50/50');
  const [membershipConversionOptions, setMembershipConversionOptions] =
    useState(null);
  const [sendAgreement, setSendAgreement] = useState(null);
  const [customBillDate, setCustomBillDate] = useState(
    moment().add(1, 'days').utc().format('YYYY-MM-DD')
  );
  const classes = useStyles();
  const [isCanceling, setIsCanceling] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [convertedMembershipId, setConvertedMembershipId] = useState<
    number | undefined
  >(undefined);
  const [showConversionErrorAlert, setShowConversionErrorAlert] =
    useState(true);

  const conversionError = useError(
    !!sendAgreement && !convertedMembershipId && startDate !== now,
    'There was an error converting the membership'
  );

  const navigate = useNavigate();

  const reasonMenuItems = eventReasons
    ? Object.entries(eventReasons).map(([subCategory, subCategoryReasons]) => [
        <ListSubheader key={subCategory}>
          <Typography variant="body1" sx={{ mt: 1.5, mb: 0.25 }}>
            {reasonTypes[subCategory] || subCategory}
          </Typography>
        </ListSubheader>,
        ...(Array.isArray(subCategoryReasons)
          ? subCategoryReasons.map(reason => (
              <MenuItem key={reason.id} value={reason.id}>
                <Typography variant="body4">-- {reason.description}</Typography>
              </MenuItem>
            ))
          : [])
      ])
    : [];

  function onError(e) {
    enqueueSnackbar(e?.toString(), {
      variant: 'error'
    });
    setIsConverting(false);
  }
  useEffect(() => {
    getAvailableMemberships();
  }, []);

  function onChoicesLoaded(r) {
    const response = r.data.data;
    // only if the customer has the "Restore Membership", pi: 111070, can they have the grandfathered membership option if its active
    let membershipConversionOptions;
    if (
      response.old_membership.purchasable_item_id != LEGACY_MB_MEMBERSHIP_PI_ID
    ) {
      membershipConversionOptions = {
        ...response,
        new_memberships: response.new_memberships.filter(
          i => i.purchasable_item != GRANDFATHERED_MEMBERSHIP_PI_ID
        )
      };
    } else {
      membershipConversionOptions = response;
    }

    setMembershipConversionOptions(membershipConversionOptions);

    const newAvailableConversionOptionsInfoArray = [];
    for (
      let i = 0;
      i < membershipConversionOptions.new_memberships.length;
      i++
    ) {
      const m = membershipConversionOptions.new_memberships[i];

      const row = {
        final_amount: m.price,
        manual_amount: m.price,
        description: m.description,
        list_price: m.price,
        idx: i,
        selected: false,
        purchasable_item: m.purchasable_item,
        founder_discount: m.founder_discount
      };
      newAvailableConversionOptionsInfoArray.push(row);
    }

    setAvailableConversionOptionsInfoArray(
      newAvailableConversionOptionsInfoArray
    );
    setIsLoading(false);
  }

  function onSetPrice(value, idx) {
    const newAvailableConversionOptionsInfoArray =
      availableConversionOptionsInfoArray;
    newAvailableConversionOptionsInfoArray[idx].manual_amount =
      parseFloat(value);
    if (discountMethod === 'manual') {
      newAvailableConversionOptionsInfoArray[idx].final_amount =
        newAvailableConversionOptionsInfoArray[idx].manual_amount;
    }
    setAvailableConversionOptionsInfoArray(
      newAvailableConversionOptionsInfoArray
    );
  }

  function onStartDateOptionChanged(e) {
    if (e.target.value === 'today') {
      setStartDateOption('today');
      setStartDate(now);
    } else if (e.target.value === 'nextBill') {
      setStartDateOption('nextBill');
      setStartDate(
        membershipConversionOptions?.old_membership?.next_bill?.slice(0, 10)
      );
    } else {
      setStartDateOption('custom');
      setStartDate(customBillDate);
    }
  }

  function onSendAgreement() {
    // to implement sending agreement
    convert_membership().then(() => {
      if (startDate !== now) {
        setSendAgreement(true);
      }
    });

    // if membership is converted today and a pending ticket is created and agreement is sent via cart,
    // otherwise new membership agreement is sent via SendWaiverLinkModal
    // if (startDate === now) {
    //   convert_membership();
    // } else {
    //   setSendAgreement(availableConversionOptionsInfoArray[idx]);
    // }
  }

  function formatPriceToTwoDecimal(e) {
    const formattedPrice = parseFloat(e).toFixed(2);
    return formattedPrice;
  }

  function onDiscountMethodChanged(e) {
    setDiscountMethod(e.target.value);
  }

  function onCreditSplitChanged(e) {
    setCreditSplit(e.target.value);
  }

  function onMembershipSelected(checked, idx) {
    const newAvailableConversionOptionsInfoArray =
      availableConversionOptionsInfoArray;
    newAvailableConversionOptionsInfoArray.map(item => (item.selected = false));
    newAvailableConversionOptionsInfoArray[idx].selected = checked;
    setChosenMembershipId(idx);
    setAvailableConversionOptionsInfoArray(
      newAvailableConversionOptionsInfoArray
    );
  }

  async function getAvailableMemberships() {
    const postVars = {
      old_membership: oldMembership.id
    };
    await callRest(
      '/pos_customer/get_conversion_options/',
      postVars,
      onChoicesLoaded,
      onError
    );
  }

  function onCancelSuccess() {
    setRefreshList(true);
    enqueueSnackbar('Membership Cancelled', {
      variant: 'success'
    });
    setModalOpen(false);
  }

  async function onCancelMembership() {
    setIsCanceling(true);

    const now = new Date();
    const nowStr = now.toISOString().slice(0, 10);

    const postVars = {
      customer_membership: oldMembership.membershipId,
      date: nowStr,
      membership_event_reason: convertReason
    };

    await callRest(
      '/pos_customer/cancel_membership/',
      postVars,
      onCancelSuccess,
      onError
    );

    setIsCanceling(false);
  }

  function onSuccess(r) {
    const pendingTicketId = r?.data?.data?.pending_ticket;
    if (pendingTicketId) {
      navigate(
        `/retail3/single-ticket/${Id2Gql('PendingTicket', pendingTicketId)}`
      );
    }

    if (r?.data?.data?.new_membership_id) {
      setConvertedMembershipId(r.data.data.new_membership_id);
    }
    setRefreshList(true);
    enqueueSnackbar('Membership Converted', {
      variant: 'success'
    });
  }

  async function convert_membership() {
    const postVars = {
      old_membership: oldMembership.membershipId,
      chosen: availableConversionOptionsInfoArray.filter(r => r.selected),
      discount_method: discountMethod === 'none' ? null : discountMethod,
      is_founder: discountMethod === 'founder' ? 1 : null,
      start_date: startDate,
      credit_split: creditSplit,
      membership_event_reason: convertReason
    };

    setIsConverting(true);
    await callRest(
      '/pos_customer/convert_membership/',
      postVars,
      onSuccess,
      onError
    );
    setIsConverting(false);
    setIsCanceling(false);
  }

  const disableConvertBtn =
    !isPaymentMethodOnFile ||
    chosenMembershipId === null ||
    convertReason === undefined;

  return (
    <Table>
      {oldMembership?.isMindBodyMembership && (
        <DialogTitle sx={{ paddingBottom: 0 }}>
          Membership Conversion
          {membershipConversionOptions &&
            ` for ${membershipConversionOptions.customer_name}`}
          <br />
        </DialogTitle>
      )}

      {!isPaymentMethodOnFile && oldMembership?.isMindBodyMembership && (
        <Box
          className={classes.root2}
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 2,
            marginBottom: 0
          }}
        >
          <Typography variant="body4" sx={{ color: 'red' }}>
            To convert have a valid default payment method
          </Typography>
          {oldMembership.convertedTo !== null && (
            <Typography variant="body4" sx={{ color: 'red' }}>
              Already converted to different membership!
            </Typography>
          )}
        </Box>
      )}
      <DialogContent style={{ padding: '0' }}>
        <div className={classes.root2}>
          {membershipConversionOptions?.partner_name && (
            <>
              <InputLabel
                id="partner-grant-select"
                className={classes.statusLabel}
              >
                Assign Remaining Credit to
              </InputLabel>
              <Select
                labelId="partner-grant-select"
                defaultValue={'50/50'}
                onChange={onCreditSplitChanged}
                value={creditSplit}
                className={classes.stateSelect}
              >
                <MenuItem value={'50/50'}>
                  50/50 Split between partners
                </MenuItem>
                <MenuItem value={'customer'}>
                  {membershipConversionOptions?.customer_name}
                </MenuItem>
                <MenuItem value={'partner'}>
                  {membershipConversionOptions?.partner_name}
                </MenuItem>
              </Select>
              <br />
            </>
          )}
          {!isLoading && (
            <Table>
              <TableRow className={classes.tableHeader}>
                <TableCell></TableCell>
                <TableCell>Membership</TableCell>
                <TableCell className={classes.priceColumn}>
                  Monthly Price
                </TableCell>
                <TableCell>Credits</TableCell>
              </TableRow>
              {membershipConversionOptions?.new_memberships?.map((m, idx) => (
                <TableRow>
                  <TableCell>
                    <Radio
                      name="membership-type-conversion-option"
                      checked={
                        availableConversionOptionsInfoArray?.length > idx &&
                        availableConversionOptionsInfoArray[idx].selected
                      }
                      inputProps={{ 'aria-label': 'controlled' }}
                      onChange={e => {
                        onMembershipSelected(e.target.checked, idx);
                      }}
                      id={`choose-${idx}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body4">{m?.description}</Typography>
                  </TableCell>
                  <TableCell className={classes.priceColumn}>
                    {discountMethod === 'manual' ? (
                      <Typography variant="body4">
                        <span
                          style={{
                            textDecoration: 'line-through',
                            color: '#ffa000',
                            marginRight: '5px'
                          }}
                        >
                          {`${formatPriceToTwoDecimal(m?.price)} `}
                        </span>
                        <TextField
                          defaultValue={formatPriceToTwoDecimal(m?.price)}
                          type={'number'}
                          style={{ width: '72px', marginLeft: '10px' }}
                          onChange={e => onSetPrice(e.target.value, idx)}
                          className={classes.manualPriceInput}
                        ></TextField>
                      </Typography>
                    ) : discountMethod === 'founder' && m?.founder_discount ? (
                      <Typography variant="body4">
                        <span
                          style={{
                            textDecoration: 'line-through',
                            color: '#ffa000',
                            marginRight: '10px'
                          }}
                        >
                          {`${formatPriceToTwoDecimal(m?.price)} `}
                        </span>
                        {formatPriceToTwoDecimal(
                          m?.price - m?.founder_discount
                        )}
                      </Typography>
                    ) : (
                      <Typography variant="body4">
                        {formatPriceToTwoDecimal(m?.price)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {m?.new_credits?.map((c, idx) => (
                      <Typography variant="body4">{`${c.num_to_grant} ${
                        c.description
                      } ${idx > 0 ? <br /> : ''}`}</Typography>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          )}
          <Grid container spacing={3} my={2}>
            <Grid item xs={6}>
              <InputLabel id="bill-date-select" className={classes.statusLabel}>
                Start Date
              </InputLabel>
              <Select
                fullWidth
                labelId="bill-date-select"
                defaultValue={now}
                onChange={onStartDateOptionChanged}
                value={startDateOption}
                className={classes.stateSelect}
                sx={{
                  backgroundColor: 'primary.25'
                }}
              >
                <MenuItem value={'today'}>Today</MenuItem>
                <MenuItem value={'custom'}>Enter New Bill Date</MenuItem>
                {membershipConversionOptions?.old_membership?.next_bill >
                  now && (
                  <MenuItem value={'nextBill'}>
                    Next Bill Date{' '}
                    {prettyDate(
                      membershipConversionOptions?.old_membership?.next_bill
                    )}
                  </MenuItem>
                )}
              </Select>
            </Grid>
            <Grid item xs={6}>
              {startDateOption == 'custom' && (
                <>
                  <InputLabel
                    id="bill-date-select"
                    className={classes.statusLabel}
                  >
                    Select Next Bill Date
                  </InputLabel>
                  <DateInput
                    fullWidth
                    value={customBillDate}
                    inputProps={{
                      min: moment().add(1, 'days').utc().format('YYYY-MM-DD')
                    }}
                    onChange={e => {
                      setStartDate(formattedDate(e));
                      setCustomBillDate(formattedDate(e));
                    }}
                  />
                </>
              )}
            </Grid>
            <Grid item xs={6}>
              <InputLabel id="discount-select" className={classes.statusLabel}>
                Discounts
              </InputLabel>
              <Select
                fullWidth
                labelId="discount-select"
                defaultValue={'none'}
                onChange={onDiscountMethodChanged}
                value={discountMethod}
                className={classes.stateSelect}
              >
                <MenuItem value={'none'}>None</MenuItem>
                {isManager() && (
                  <MenuItem value={'manual'}>Manual Price Override</MenuItem>
                )}
              </Select>
            </Grid>
            <Grid item xs={6}>
              <InputLabel id="discount-select" className={classes.statusLabel}>
                Reason
              </InputLabel>
              <Select
                fullWidth
                labelId="reason-select"
                defaultValue={convertReason}
                onChange={e => setConvertReason(e.target.value)}
                value={convertReason}
                renderValue={selected => {
                  const selectedReason = Object.values(eventReasons)
                    .flat()
                    .find(
                      reason =>
                        (reason as { id: string; description: string }).id ===
                        selected
                    );
                  return selectedReason
                    ? (selectedReason as { id: string; description: string })
                        .description
                    : '';
                }}
                className={classes.stateSelect}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                      overflowY: 'auto'
                    }
                  }
                }}
              >
                {reasonMenuItems}
              </Select>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <ModalActions>
        <Button
          variant="outlined"
          style={{
            marginLeft: 10,
            height: 60
          }}
          disabled={isConverting || isCanceling}
          onClick={() => {
            setModalOpen(false);
          }}
        >
          Keep Membership Unchanged
        </Button>
        {oldMembership?.isMindBodyMembership && (
          <LoadingButton
            variant="outlined"
            loading={isCanceling}
            onClick={onCancelMembership}
            disabled={convertReason === undefined}
            style={{
              marginLeft: 10,
              height: 60
            }}
          >
            Cancel Mindbody Membership
          </LoadingButton>
        )}
        <LoadingButton
          type="submit"
          variant="contained"
          loading={isConverting}
          disabled={disableConvertBtn}
          onClick={onSendAgreement}
          style={{
            marginLeft: 10,
            height: 60
          }}
        >
          Convert
        </LoadingButton>
      </ModalActions>

      {!!sendAgreement && !!convertedMembershipId && startDate !== now && (
        <MembershipAgreementModal
          customer={{
            guid: membershipConversionOptions?.customer_guid,
            mobilePhone: membershipConversionOptions?.customer_phone,
            email: membershipConversionOptions?.customer_email,
            id: membershipConversionOptions?.customer_id
          }}
          onAgreementSend={() => {
            setSendAgreement(false);
            setModalOpen();
          }}
          dataSource={[
            {
              what: 'membership_agreement',
              membership_id: convertedMembershipId?.toString()
            }
          ]}
        />
      )}
      <Alert
        title="Membership Conversion"
        messages={[conversionError]}
        open={!!conversionError && showConversionErrorAlert}
        onClose={() => {
          setShowConversionErrorAlert(false);
          setIsLoading(false);
          setIsConverting(false);
        }}
        variant="error"
      />
    </Table>
  );
};

export default ConvertMembershipModal;
